import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";
import { TabPanel, TabView } from "primereact/tabview";
import { useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";

import BaseRateFilesTable from "./components/BaseRateFilesTable";
import ConfigsTable from "./components/ConfigsTable";
import ContractsTable from "./components/ContractsTable";
import CustomCptVolumeTable from "./components/CustomCptVolumeTable";
import EdiConversionFilesTable from "./components/EdiConversionFilesTable";
import MarketPricesTable from "./components/MarketPricesTable";
import PayerRulesTable from "./components/PayerRulesTable";
import PayersTable from "./components/PayersTable";
import GrouperRateFile from "./tools/GrouperRateFile";
import RateExpander from "./tools/RateExpander";
import RateFileSplitter from "./tools/RateFileSplitter";
import ServiceLineLibraries from "./tools/ServiceLineLibraries";
import ServiceLineSynonyms from "./tools/ServiceLineSynonyms";
import {
  BASE_RATE_SUBTAB_CONFIGS,
  BASE_RATE_SUBTAB_CUSTOM_CPT_VOLUME,
  BASE_RATE_SUBTAB_FILES,
  BASE_RATE_TAB_INDEX,
  TOOLS_SUBTAB_EXPANDER,
  TOOLS_SUBTAB_LIBRARIES,
  TOOLS_SUBTAB_SPLITTER,
  TOOLS_SUBTAB_SYNONYMS,
  TOOLS_SUBTABS,
  TOOLS_TAB_INDEX,
  ToolsSubtabValue,
} from "./utils/constants";

const StyledButton = styled(Button)`
  gap: ${sizer(2)};
`;

const StyledMenu = styled(Menu)`
  .p-submenu-header {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }
  .p-menuitem-link {
    padding: 0.5rem 1rem !important;
  }
  .p-menuitem-text {
    font-size: 14px !important;
    color: var(--gray-700) !important;
  }
`;

const FlexWrapper = styled.div`
  display: flex;
  justify-content: end;
  margin-bottom: ${sizer(3)};
`;

const BASE_RATE_SUBTABS = [
  BASE_RATE_SUBTAB_FILES,
  BASE_RATE_SUBTAB_CONFIGS,
  BASE_RATE_SUBTAB_CUSTOM_CPT_VOLUME,
] as const;

export function PayerRules() {
  const navigate = useNavigate();
  const newRuleMenuRef = useRef<Menu>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabIndex = Number(searchParams.get("tab"));
  const baseSubtab = searchParams.get("baseSubtab") ?? BASE_RATE_SUBTAB_FILES;
  const baseRateSubtabIndex = Math.max(
    0,
    BASE_RATE_SUBTABS.indexOf(baseSubtab as (typeof BASE_RATE_SUBTABS)[number])
  );

  const toolsSubtab =
    (searchParams.get("toolsSubtab") as ToolsSubtabValue | null) ??
    TOOLS_SUBTAB_SPLITTER;
  const toolsSubtabIndex = Math.max(
    0,
    TOOLS_SUBTABS.indexOf(toolsSubtab as (typeof TOOLS_SUBTABS)[number])
  );

  const handleBaseRateSubtabChange = (index: number) => {
    const nextSubtab = BASE_RATE_SUBTABS[index] ?? BASE_RATE_SUBTAB_FILES;
    setSearchParams({
      tab: String(BASE_RATE_TAB_INDEX),
      baseSubtab: nextSubtab,
    });
  };

  const handleToolsSubtabChange = (index: number) => {
    const nextSubtab = TOOLS_SUBTABS[index] ?? TOOLS_SUBTAB_SPLITTER;
    setSearchParams({
      tab: String(TOOLS_TAB_INDEX),
      toolsSubtab: nextSubtab,
    });
  };

  const goToSynonymsSubtab = (_payerId?: number) => {
    // Deep-link to the Synonyms sub-tab. (Payer-id pre-filter is a follow-up; the
    // synonyms sub-tab already has a payer-filter dropdown, so the analyst can pick
    // it in two clicks.)
    setSearchParams({
      tab: String(TOOLS_TAB_INDEX),
      toolsSubtab: TOOLS_SUBTAB_SYNONYMS,
    });
  };

  const newRuleMenuItems = useMemo<MenuItem[]>(
    () => [
      {
        label: "Select Rule Type",
        items: [
          { label: "MPPR", command: () => navigate("/payer-rules/mppr/new") },
          {
            label: "Case Rate",
            command: () => navigate("/payer-rules/case-rate/new"),
          },
          {
            label: "Package",
            command: () => navigate("/payer-rules/packages/new"),
          },
          {
            label: "Exploding Fee Schedule",
            command: () => navigate("/payer-rules/exploding-fee-schedules/new"),
          },
          {
            label: "Hierarchy",
            command: () => navigate("/payer-rules/hierarchy-rules/new"),
          },
          {
            label: "Professional",
            command: () => navigate("/payer-rules/professional-rulesets/new"),
          },
          {
            label: "Institutional",
            command: () => navigate("/payer-rules/institutional-rulesets/new"),
          },
          {
            label: "Professional Modifiers",
            command: () => navigate("/payer-rules/professional-modifiers/new"),
          },
          {
            label: "Lesser-Of Charges",
            command: () => navigate("/payer-rules/lesser-of-charges/new"),
          },
          {
            label: "Charge-Based Pricing",
            command: () => navigate("/payer-rules/charge-based-pricing/new"),
          },
          {
            label: "Unit Cap (Once Per Service)",
            command: () => navigate("/payer-rules/unit-cap/new"),
          },
        ],
      },
      {
        label: "Inpatient Rules",
        items: [
          {
            label: "Pass Through",
            command: () =>
              navigate("/payer-rules/inpatient-rulesets/new?type=PASS_THROUGH"),
          },
          {
            label: "Transfer",
            command: () =>
              navigate("/payer-rules/inpatient-rulesets/new?type=TRANSFER"),
          },
          {
            label: "Inpatient Outlier Rules",
            command: () =>
              navigate(
                "/payer-rules/inpatient-rulesets/new?type=INPATIENT_OUTLIER"
              ),
          },
        ],
      },
    ],
    [navigate]
  );

  return (
    <TabView
      className="w-full"
      activeIndex={activeTabIndex}
      onTabChange={(e) => {
        if (e.index === BASE_RATE_TAB_INDEX) {
          setSearchParams({
            tab: e.index.toString(),
            baseSubtab: BASE_RATE_SUBTABS[baseRateSubtabIndex],
          });
        } else if (e.index === TOOLS_TAB_INDEX) {
          setSearchParams({
            tab: e.index.toString(),
            toolsSubtab: TOOLS_SUBTABS[toolsSubtabIndex],
          });
        } else {
          setSearchParams({ tab: e.index.toString() });
        }
      }}
    >
      <TabPanel header="Adjustment Rules">
        <FlexWrapper>
          <StyledButton
            icon="pi pi-plus"
            className="p-button-raised"
            aria-controls="popup_menu_right"
            aria-haspopup
            onClick={(event) => newRuleMenuRef.current?.toggle(event)}
          >
            New Payer Rule
          </StyledButton>
          <StyledMenu
            model={newRuleMenuItems}
            popup
            ref={newRuleMenuRef}
            id="popup_menu_right"
            appendTo="self"
          />
        </FlexWrapper>
        {/* CLE-2524 — Live Rules is everything the pricing engine applies
            (payer-provider + fallback); Typical Rules are inert templates. */}
        <TabView>
          <TabPanel header="Live Rules">
            <PayerRulesTable mode="live" />
          </TabPanel>
          <TabPanel header="Typical Rules">
            <PayerRulesTable mode="typical" />
          </TabPanel>
        </TabView>
      </TabPanel>
      <TabPanel header="Payers">
        <FlexWrapper>
          <StyledButton
            icon="pi pi-plus"
            className="p-button-raised"
            onClick={() => navigate("/payer-rules/payer/new")}
          >
            New Payer
          </StyledButton>
        </FlexWrapper>
        <PayersTable />
      </TabPanel>
      <TabPanel header="Source Fee Schedules">
        <TabView
          activeIndex={baseRateSubtabIndex}
          onTabChange={(e) => handleBaseRateSubtabChange(e.index)}
        >
          <TabPanel header="Base Rate Files">
            <BaseRateFilesTable />
          </TabPanel>
          <TabPanel header="Base Rate Configs">
            <ConfigsTable />
          </TabPanel>
          <TabPanel header="Custom CPT Volume">
            <CustomCptVolumeTable />
          </TabPanel>
        </TabView>
      </TabPanel>
      <TabPanel header="Contracts">
        <FlexWrapper>
          <StyledButton
            icon="pi pi-plus"
            className="p-button-raised"
            onClick={() => navigate("/payer-rules/contracts/new")}
          >
            New Contract
          </StyledButton>
        </FlexWrapper>
        <ContractsTable />
      </TabPanel>
      <TabPanel header="Market Price Datasets">
        <MarketPricesTable />
      </TabPanel>
      <TabPanel header="EDI Files">
        <EdiConversionFilesTable />
      </TabPanel>
      <TabPanel header="Rate File Tools">
        <TabView
          activeIndex={toolsSubtabIndex}
          onTabChange={(e) => handleToolsSubtabChange(e.index)}
        >
          <TabPanel header="Splitter">
            <RateFileSplitter />
          </TabPanel>
          <TabPanel header="Expander">
            <RateExpander onNavigateToSynonyms={goToSynonymsSubtab} />
          </TabPanel>
          <TabPanel header="Groupers">
            <GrouperRateFile />
          </TabPanel>
          <TabPanel header="Service Line Synonyms">
            <ServiceLineSynonyms />
          </TabPanel>
          <TabPanel header="Service Line Libraries">
            <ServiceLineLibraries />
          </TabPanel>
        </TabView>
      </TabPanel>
    </TabView>
  );
}
